import React, { useState, useMemo, useEffect } from 'react';
import { useStore } from '../../store/StoreContext';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { IconPlus, IconTrash } from '../../components/icons';
import { NumberField, ChoiceKeys, Switch, Button, IconButton, StatusPill, tableWrapCls, tableCls, thCls, trCls, trOpenCls, tdCls, tdMutedCls } from '../../components/ui';

// Unit-state colours for the in-table StatusPill (lists.md §1/§3) — mirrors UNIT_STATE_STYLE.
const UNIT_STATE_COLOR = { empty: '#059669', quoted: '#d97706', occupied: '#4f46e5', internal: '#64748b', retired: '#94a3b8' };
import db from '../../lib/database';
import { STORAGE_SIZES, buildOccupancy, unitState, unitRate, tenantName, byId, UNIT_STATE_STYLE } from '../../lib/storageHelpers';

// ===== SETTINGS → STORAGE (sql/086, Jaylen 2026-09-08) =====
// The "our storage" setup lives here so it can grow without code:
//   • Facilities — Extra Space Hendersonville is the default BBM facility
//     (its address auto-fills estimates). Monthly lease = what BBM pays
//     for the whole block. Size list = the CLIENT RATE CARD — what a
//     tenant is billed per unit; new tenancies default their billed
//     rate from it.
//   • Gate code — separate table, RLS'd to owner/super_admin. Other roles
//     never receive it (the fetch returns nothing), so the panel hides.
//   • Unit inventory — every room we hold: unit #, size, cost override,
//     rental vs BBM-internal use (packing school, community space). Retire
//     a unit when we give it back; history stays intact.

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500';
const smallInput = 'px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

function FacilityEditor({ facility, onSave, onCancel, saving }) {
    const [form, setForm] = useState(() => ({
        name: facility?.name || '', address: facility?.address || '', notes: facility?.notes || '',
        monthly_lease_cost: facility?.monthly_lease_cost ?? '',
        is_default: !!facility?.is_default, active: facility?.active ?? true,
        rates: Object.entries(facility?.size_rates || {}).map(([size, rate]) => ({ size, rate })),
    }));
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setRate = (i, k, v) => setForm(f => ({ ...f, rates: f.rates.map((r, j) => j === i ? { ...r, [k]: v } : r) }));
    const missingSizes = STORAGE_SIZES.filter(s => !form.rates.some(r => r.size === s));

    const submit = () => {
        if (!form.name.trim()) return;
        const size_rates = {};
        form.rates.forEach(r => { const n = parseFloat(r.rate); if (r.size.trim() && !isNaN(n)) size_rates[r.size.trim()] = n; });
        onSave({ ...(facility?.id ? { id: facility.id } : {}), name: form.name.trim(), address: form.address.trim(), notes: form.notes, is_default: form.is_default, active: form.active, size_rates,
            monthly_lease_cost: form.monthly_lease_cost === '' ? null : parseFloat(form.monthly_lease_cost) || null });
    };

    return (
        <div className="border border-red-200 bg-red-50/40 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold text-slate-600">Facility name</label>
                    <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Extra Space Hendersonville" autoFocus />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-600">Address <span className="font-normal text-slate-400">— auto-fills estimates</span></label>
                    <input className={inputCls} value={form.address} onChange={e => set('address', e.target.value)} placeholder="137 Sugarloaf Rd, Hendersonville, NC 28792" />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <label className="text-xs font-bold text-slate-600">Monthly lease <span className="font-normal text-slate-400">— what BBM pays for the whole block</span></label>
                    <NumberField step={5} min={0} prefix="$" value={form.monthly_lease_cost} onChange={v => set('monthly_lease_cost', v)} placeholder="4186" ariaLabel="Monthly lease" />
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-slate-600">Client rate card by size <span className="font-normal text-slate-400">— what a tenant is billed per unit; new tenants default to this</span></label>
                <div className="flex flex-wrap gap-2 mt-1">
                    {form.rates.map((r, i) => (
                        <div key={i} className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
                            <input className={smallInput + ' w-20'} value={r.size} onChange={e => setRate(i, 'size', e.target.value)} placeholder="10x10" />
                            <NumberField size="sm" step={5} min={0} prefix="$" className="w-[132px]" value={r.rate} onChange={v => setRate(i, 'rate', v)} placeholder="225" ariaLabel={`Monthly rate for ${r.size || 'size'}`} />
                            <IconButton size="sm" tone="danger" label={`Remove ${r.size || 'size'}`} onClick={() => set('rates', form.rates.filter((_, j) => j !== i))}><IconTrash size={13}/></IconButton>
                        </div>
                    ))}
                    {missingSizes.map(s => (
                        <Button key={s} variant="tertiary" size="sm" onClick={() => set('rates', [...form.rates, { size: s, rate: '' }])}>+ {s}</Button>
                    ))}
                    <Button variant="tertiary" size="sm" onClick={() => set('rates', [...form.rates, { size: '', rate: '' }])}>+ other size</Button>
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-slate-600">Notes</label>
                <input className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Office hours, where to park the truck…" />
            </div>
            <div className="flex items-center gap-4 text-sm">
                <Switch checked={!!form.is_default} onChange={v => set('is_default', v)} label="Default BBM facility" description="Pre-selected on estimates" />
                <Switch checked={!!form.active} onChange={v => set('active', v)} label="Active" />
            </div>
            <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                <Button variant="primary" onClick={submit} loading={saving} disabled={!form.name.trim()}>Save facility</Button>
            </div>
        </div>
    );
}

// Gate code panel — only renders content when the fetch returns a row
// (RLS: owner/super_admin). Everyone else sees nothing at all.
function GateCodePanel({ facility }) {
    const { isSuperAdmin } = useAuth();
    const { show: showToast } = useToast();
    const [row, setRow] = useState(undefined); // undefined = loading, null = not visible
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ gate_code: '', notes: '' });
    const [reveal, setReveal] = useState(false);

    useEffect(() => {
        let alive = true;
        if (!isSuperAdmin) { setRow(null); return; }
        db.getStorageFacilityCode(facility.id)
            .then(r => { if (alive) { setRow(r || { gate_code: '', notes: '' }); setForm({ gate_code: r?.gate_code || '', notes: r?.notes || '' }); } })
            .catch(() => { if (alive) setRow(null); });
        return () => { alive = false; };
    }, [facility.id, isSuperAdmin]);

    if (!isSuperAdmin || row === null || row === undefined) return null;

    const save = async () => {
        try {
            const saved = await db.saveStorageFacilityCode(facility.id, form);
            setRow(saved); setEditing(false); showToast('Gate code saved');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    return (
        <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">🔑 Gate code <span className="font-normal normal-case text-slate-400">— owner &amp; super admin only</span></div>
                {!editing && <Button variant="tertiary" size="sm" onClick={() => setEditing(true)}>edit</Button>}
            </div>
            {editing ? (
                <div className="mt-2 space-y-2">
                    <input className={inputCls + ' font-mono'} value={form.gate_code} onChange={e => setForm(f => ({ ...f, gate_code: e.target.value }))} placeholder="*1234#" />
                    <input className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Other codes / lock combos / where the keypad is" />
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => { setEditing(false); setForm({ gate_code: row.gate_code || '', notes: row.notes || '' }); }}>Cancel</Button>
                        <Button variant="primary" size="sm" onClick={save}>Save</Button>
                    </div>
                </div>
            ) : (
                <div className="mt-1.5 flex items-center gap-3 text-sm">
                    <span className="font-mono font-bold text-slate-800 tracking-wider">{row.gate_code ? (reveal ? row.gate_code : '•'.repeat(Math.min(row.gate_code.length, 10))) : <span className="text-slate-400 font-sans font-normal">not set</span>}</span>
                    {row.gate_code && <Button variant="tertiary" size="sm" onClick={() => setReveal(r => !r)}>{reveal ? 'hide' : 'show'}</Button>}
                    {row.notes && reveal && <span className="text-xs text-slate-500">{row.notes}</span>}
                </div>
            )}
        </div>
    );
}

function UnitInventory({ facility, units, tenancies, clients }) {
    const { saveStorageUnit, patchStorageUnit, removeStorageUnit } = useStore();
    const { show: showToast } = useToast();
    const { confirm, confirmElement } = useConfirm();
    const [draft, setDraft] = useState(null);
    const [showRetired, setShowRetired] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const occupancy = useMemo(() => buildOccupancy(tenancies), [tenancies]);
    const clientsById = useMemo(() => byId(clients), [clients]);
    const list = units.filter(u => showRetired || u.active);

    const addDraft = () => setDraft({ unit_number: '', size: STORAGE_SIZES[0], monthly_cost: '', kind: 'rental', internal_label: '', notes: '' });
    const saveDraft = async () => {
        if (!draft.unit_number.trim()) return;
        try {
            await saveStorageUnit({ facility_id: facility.id, unit_number: draft.unit_number, size: draft.size, monthly_cost: draft.monthly_cost, kind: draft.kind, internal_label: draft.kind === 'internal' ? draft.internal_label : '', notes: draft.notes, active: true });
            showToast(`Unit ${draft.unit_number} added`);
            setDraft(null);
        } catch (e) { showToast(e.code === '23505' ? `Unit ${draft.unit_number} already exists here` : 'Error: ' + e.message, 'error'); }
    };
    const startEdit = (u) => { setEditingId(u.id); setEditForm({ unit_number: u.unit_number, size: u.size, monthly_cost: u.monthly_cost ?? '', kind: u.kind, internal_label: u.internal_label || '', notes: u.notes || '' }); };
    const saveEdit = async () => {
        try {
            await patchStorageUnit(editingId, { ...editForm, internal_label: editForm.kind === 'internal' ? editForm.internal_label : '' });
            setEditingId(null); showToast('Unit updated');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const toggleRetire = async (u) => {
        if (u.active && occupancy[u.id]) { showToast('Unit is occupied — close or move the tenancy first', 'error'); return; }
        try { await patchStorageUnit(u.id, { active: !u.active }); showToast(u.active ? `Unit ${u.unit_number} retired` : `Unit ${u.unit_number} restored`); }
        catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const remove = async (u) => {
        if (occupancy[u.id]) { showToast('Unit is occupied — close or move the tenancy first', 'error'); return; }
        const choice = await confirm({
            title: `Delete unit ${u.unit_number}?`,
            body: `Unit ${u.unit_number} is removed from the facility and any past tenancies lose their unit link. Delete only if this row was a mistake — retiring keeps the history and hides it from new estimates.`,
            confirmLabel: `Delete unit ${u.unit_number}`,
            danger: true,
            secondaryLabel: u.active ? 'Retire instead' : undefined,
        });
        if (choice === 'secondary') { await toggleRetire(u); return; }
        if (!choice) return;
        try { await removeStorageUnit(u.id); showToast('Unit deleted'); } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    const counts = useMemo(() => {
        const c = { empty: 0, quoted: 0, occupied: 0, internal: 0, retired: 0 };
        units.forEach(u => { c[unitState(u, occupancy)]++; });
        return c;
    }, [units, occupancy]);

    const rowEditor = (form, setF, onSave, onCancel) => (
        <tr className={`${trCls} ${trOpenCls}`}>
            <td className={tdCls}><input className={smallInput + ' w-20'} value={form.unit_number} onChange={e => setF(f => ({ ...f, unit_number: e.target.value }))} placeholder="#" autoFocus /></td>
            <td className={tdCls}>
                <input list="bbm-storage-sizes" className={smallInput + ' w-24'} value={form.size} onChange={e => setF(f => ({ ...f, size: e.target.value }))} placeholder="10x10" />
            </td>
            <td className={tdCls}><NumberField size="sm" step={5} min={0} prefix="$" className="w-[132px]" value={form.monthly_cost} onChange={v => setF(f => ({ ...f, monthly_cost: v }))} placeholder={`${parseFloat(facility.size_rates?.[form.size]) || '—'}`} ariaLabel="Monthly rate override" /></td>
            <td className={tdCls}>
                <div className="flex items-center gap-1">
                    <ChoiceKeys size="sm" wrap={false} items={[{ value: 'rental', label: 'Rental' }, { value: 'internal', label: 'BBM use' }]} value={form.kind} onChange={v => setF(f => ({ ...f, kind: v }))} ariaLabel="Unit use" />
                    {form.kind === 'internal' && <input className={smallInput + ' w-40'} value={form.internal_label} onChange={e => setF(f => ({ ...f, internal_label: e.target.value }))} placeholder="Packing school" />}
                </div>
            </td>
            <td className={tdCls}><input className={smallInput + ' w-full'} value={form.notes} onChange={e => setF(f => ({ ...f, notes: e.target.value }))} placeholder="notes" /></td>
            <td className={`${tdCls} text-right whitespace-nowrap`}>
                <Button variant="tertiary" size="sm" onClick={onCancel}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={onSave} disabled={!form.unit_number.trim()}>Save</Button>
            </td>
        </tr>
    );

    return (
        <div className="mt-4">
            <datalist id="bbm-storage-sizes">{STORAGE_SIZES.map(s => <option key={s} value={s} />)}</datalist>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-bold text-slate-700">{units.filter(u => u.active).length} units</span>
                    {['empty', 'occupied', 'quoted', 'internal'].filter(k => counts[k] > 0).map(k => (
                        <span key={k} className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${UNIT_STATE_STYLE[k].cls}`}>{counts[k]} {UNIT_STATE_STYLE[k].label.toLowerCase()}</span>
                    ))}
                    {counts.retired > 0 && (
                        <Button variant="tertiary" size="sm" onClick={() => setShowRetired(s => !s)}>{showRetired ? 'hide' : 'show'} {counts.retired} retired</Button>
                    )}
                </div>
                {!draft && <Button variant="primary" size="sm" onClick={addDraft} leftIcon={<IconPlus size={14}/>}>Add unit</Button>}
            </div>
            {/* Units table — ledger recipe, see BBM Context for claude/lists.md §3 */}
            <div className={tableWrapCls}>
                <table className={tableCls}>
                    <thead>
                        <tr>
                            <th className={thCls}>Unit</th>
                            <th className={thCls}>Size</th>
                            <th className={thCls}>Rate</th>
                            <th className={thCls}>Use</th>
                            <th className={thCls}>Who / notes</th>
                            <th className={thCls}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {draft && rowEditor(draft, setDraft, saveDraft, () => setDraft(null))}
                        {list.map(u => {
                            if (editingId === u.id) return <React.Fragment key={u.id}>{rowEditor(editForm, setEditForm, saveEdit, () => setEditingId(null))}</React.Fragment>;
                            const state = unitState(u, occupancy);
                            const t = occupancy[u.id];
                            const style = UNIT_STATE_STYLE[state];
                            return (
                                <tr key={u.id} className={`${trCls} ${u.active ? '' : 'opacity-50'}`}>
                                    <td className={`${tdCls} font-mono tabular-nums font-bold text-slate-800`}>{u.unit_number}</td>
                                    <td className={tdMutedCls}>{u.size || '—'}</td>
                                    <td className={`${tdCls} text-slate-700 font-mono tabular-nums`}>${unitRate(u, facility)}{u.monthly_cost != null && <span className="text-[10px] text-slate-400 ml-1 font-sans" title="Overrides the rate card for this unit">override</span>}</td>
                                    <td className={tdCls}><StatusPill label={state === 'internal' ? (u.internal_label || 'BBM use') : style.label} color={UNIT_STATE_COLOR[state] || '#64748b'} /></td>
                                    <td className={`${tdMutedCls} text-xs`}>
                                        {t && <span className="font-semibold text-slate-800">{tenantName(t, clientsById)}</span>}
                                        {t && u.notes && <span className="text-slate-300"> · </span>}
                                        {u.notes && <span className="text-slate-500 italic">{u.notes}</span>}
                                    </td>
                                    <td className={`${tdCls} text-right whitespace-nowrap text-xs`}>
                                        <Button variant="tertiary" size="sm" onClick={() => startEdit(u)}>edit</Button>
                                        <Button variant={u.active ? 'danger' : 'tertiary'} size="sm" onClick={() => toggleRetire(u)}>{u.active ? 'retire' : 'restore'}</Button>
                                        {!u.active && <IconButton size="sm" tone="danger" label={`Delete unit ${u.unit_number}`} onClick={() => remove(u)}><IconTrash size={13}/></IconButton>}
                                    </td>
                                </tr>
                            );
                        })}
                        {list.length === 0 && !draft && (
                            <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-sm">No units yet — add the rooms BBM rents here.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {confirmElement}
        </div>
    );
}

export default function StorageSettingsManager() {
    const { storageFacilities, storageUnits, storageTenancies, clients, saveStorageFacility, removeStorageFacility } = useStore();
    const { show: showToast } = useToast();
    const { confirm, confirmElement } = useConfirm();
    const [editing, setEditing] = useState(null); // 'new' | facility
    const [saving, setSaving] = useState(false);

    const onSave = async (f) => {
        setSaving(true);
        try {
            const saved = await saveStorageFacility(f);
            // Only one default — demote the others client-side + server-side.
            if (saved.is_default) {
                for (const other of storageFacilities.filter(x => x.is_default && x.id !== saved.id)) {
                    await saveStorageFacility({ ...other, is_default: false });
                }
            }
            showToast('Facility saved'); setEditing(null);
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setSaving(false); }
    };
    const onDelete = async (f) => {
        const unitCount = storageUnits.filter(u => u.facility_id === f.id).length;
        if (!(await confirm({ title: `Delete ${f.name}?`, body: unitCount ? `${f.name} and its ${unitCount} unit${unitCount === 1 ? '' : 's'} are removed. Tenancies keep their history but lose the facility link. This can't be undone.` : `${f.name} has no units. It is removed from the facility list. This can't be undone.`, confirmLabel: `Delete ${f.name}`, danger: true }))) return;
        try { await removeStorageFacility(f.id); showToast('Facility deleted'); } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">Storage</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Where BBM stores things, the rooms we rent, the lease we pay, and the rate card tenants are billed. Estimates pick from the empty units here; the Storage page shows who's in them.</p>
                </div>
                {editing !== 'new' && <Button variant="primary" onClick={() => setEditing('new')} leftIcon={<IconPlus size={16}/>}>Add facility</Button>}
            </div>

            {editing === 'new' && <FacilityEditor facility={null} saving={saving} onSave={onSave} onCancel={() => setEditing(null)} />}

            {storageFacilities.length === 0 && editing !== 'new' && (
                <div className="border border-dashed border-slate-300 rounded-xl py-8 text-center text-slate-400 text-sm">No facilities yet. Add Extra Space to get started.</div>
            )}

            {storageFacilities.map(f => (
                <div key={f.id} className={`border rounded-xl p-4 ${f.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
                    {editing && editing !== 'new' && editing.id === f.id ? (
                        <FacilityEditor facility={f} saving={saving} onSave={onSave} onCancel={() => setEditing(null)} />
                    ) : (
                        <>
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900">{f.name}</span>
                                        {f.is_default && <span className="text-[10px] font-black uppercase tracking-wide bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Default</span>}
                                        {!f.active && <span className="text-[10px] font-black uppercase tracking-wide bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Inactive</span>}
                                    </div>
                                    <div className="text-xs text-slate-500">{f.address || 'No address yet'}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {f.monthly_lease_cost != null && <span>Lease: <b className="text-slate-700">${Number(f.monthly_lease_cost).toLocaleString()}/mo</b></span>}
                                        {f.monthly_lease_cost != null && Object.keys(f.size_rates || {}).length > 0 && <span> · </span>}
                                        {Object.keys(f.size_rates || {}).length > 0 && <span>Rate card: {Object.entries(f.size_rates).map(([s, r]) => `${s} $${r}`).join(' · ')}</span>}
                                    </div>
                                    {f.notes && <div className="text-xs text-slate-400 italic mt-0.5">{f.notes}</div>}
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <Button variant="tertiary" size="sm" onClick={() => setEditing(f)}>edit</Button>
                                    <Button variant="danger" size="sm" onClick={() => onDelete(f)}>delete</Button>
                                </div>
                            </div>
                            <GateCodePanel facility={f} />
                            <UnitInventory facility={f} units={storageUnits.filter(u => u.facility_id === f.id)} tenancies={storageTenancies} clients={clients} />
                        </>
                    )}
                </div>
            ))}
            {confirmElement}
        </div>
    );
}
